/**
 * A standalone React modal component for displaying the Student Helper informational
 * slideshow.
 */

import { useState } from "react"

import { Dialog, DialogContent } from "@/components/ui/Dialog"
import { Link } from "@/components/ui/Link"

import slide1 from "./assets/studenthelper_slide1.png"
import slide2 from "./assets/studenthelper_slide2.png"
import slide3 from "./assets/studenthelper_slide3.png"

const slides = [slide1, slide2, slide3]

interface StudentHelperInfoModalProps {
  isOpen: boolean
  onClose: () => void
}

export function StudentHelperInfoModal({
  isOpen,
  onClose,
}: StudentHelperInfoModalProps) {
  const [currentIndex, setCurrentIndex] = useState(0)

  function goToPrev() {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? slides.length - 1 : prevIndex - 1,
    )
  }

  function goToNext() {
    setCurrentIndex((prevIndex) =>
      prevIndex === slides.length - 1 ? 0 : prevIndex + 1,
    )
  }

  function goToIndex(index: number) {
    setCurrentIndex(index)
  }

  const currentSlide = slides[currentIndex]

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setCurrentIndex(0)
          onClose()
        }
      }}
    >
      <DialogContent className="rainbow-gradient-border tw-rounded-sm tw-p-1 tw-w-auto tw-max-w-[90vw]">
        <div className="tw-relative tw-bg-white tw-rounded-sm tw-p-4">
          <div className="tw-relative tw-w-full tw-aspect-[936/630] tw-px-10">
            <Link
              href="https://www.classkick.com/"
              external={true}
              className="tw-flex tw-items-center tw-justify-center tw-h-full"
            >
              <img
                src={currentSlide}
                alt={`Slide ${currentIndex + 1}`}
                className="tw-max-w-full tw-max-h-full tw-object-contain"
              />
            </Link>

            {/* Navigation Container */}
            <div className="tw-absolute tw-bottom-4 tw-left-0 tw-right-0 tw-flex tw-items-center tw-justify-center">
              {/* Previous Button */}
              <button
                className="tw-h-10 tw-w-10 tw-rounded-full tw-bg-gray-100 hover:tw-bg-gray-200 tw-flex tw-items-center tw-justify-center"
                aria-label="Previous Slide"
                onClick={goToPrev}
              >
                <svg
                  className="tw-h-5 tw-w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              {/* Dot Indicators */}
              <div className="tw-flex tw-items-center tw-space-x-2 tw-mx-4">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    className={`tw-h-2 tw-w-2 tw-rounded-full ${currentIndex === index ? "tw-bg-gray-800" : "tw-bg-gray-300 tw-hover:bg-gray-400"}`}
                    aria-label={`Go to slide ${index + 1}`}
                    onClick={() => goToIndex(index)}
                  ></button>
                ))}
              </div>

              {/* Next Button */}
              <button
                className="tw-h-10 tw-w-10 tw-rounded-full tw-bg-gray-100 hover:tw-bg-gray-200 tw-flex tw-items-center tw-justify-center"
                aria-label="Next Slide"
                onClick={goToNext}
              >
                <svg
                  className="tw-h-5 tw-w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
